import React, { Fragment } from "react";
import { ReactTabulator } from "react-tabulator";
import i18n from "../../i18n";
import LanguageAwarePureComponent from "./LanguageAwarePureComponent";

/**
 * Maps i18n language codes to proper locale codes for ReactTabulator
 */
const getLocaleForTable = () => {
  const currentLang = i18n.language || 'en';
  // Map 'no' to 'nb-NO' for proper Norwegian character sorting (æ, ø, å)
  return currentLang === 'no' ? 'nb-NO' : currentLang;
};

class UserTable extends LanguageAwarePureComponent {
  ref = React.createRef();
  _isMounted = false;

  getColumns = () => {
    const { t } = this.props;
    return [
      { 
        title: t("userManagement.viewUsers.uniqueId"), 
        field: "uniqueId", 
        width: 150, 
        headerFilter: "input",
        headerFilterPlaceholder: t("userManagement.viewUsers.search.placeholder")
      },
      { 
        title: t("userManagement.viewUsers.firstName"), 
        field: "firstName", 
        headerFilter: "input",
        headerFilterPlaceholder: t("userManagement.viewUsers.search.placeholder")
      },
      { 
        title: t("userManagement.viewUsers.lastName"), 
        field: "lastName", 
        headerFilter: "input",
        headerFilterPlaceholder: t("userManagement.viewUsers.search.placeholder")
      },
      { 
        title: t("userManagement.viewUsers.contactNumber"), 
        field: "contactNumber", 
        width: 150, 
        headerFilter: "input",
        headerFilterPlaceholder: t("userManagement.viewUsers.search.placeholder")
      },
      { 
        title: t("userManagement.viewUsers.email"), 
        field: "email", 
        width: 175, 
        headerFilter: "input",
        headerFilterPlaceholder: t("userManagement.viewUsers.search.placeholder")
      },
      {
        title: t("userManagement.addUser.active"),
        field: "active",
        align: "center",
        formatter: "tickCross",
        width: 75,
        formatterParams: {
          allowEmpty: false,
          allowTruthy: true,
          tickElement: "<i class='fa fa-check' style='color:#0ad6a3'></i>",
          crossElement: "<i class='fa fa-times' style='color:#ff474c'></i>"
        }
      }
    ];
  };

  state = {
    t: this.props.t,
    columns: this.getColumns(),
    data: this.props.data,
    options: {
      pagination: "local",
      paginationSize: 10,
      locale: getLocaleForTable(),
      langs: {
        all: {
          pagination: {
            first: this.props.t("common.table.pagination.first"),
            first_title: this.props.t("common.table.pagination.first_title"),
            last: this.props.t("common.table.pagination.last"),
            last_title: this.props.t("common.table.pagination.last_title"),
            prev: this.props.t("common.table.pagination.prev"),
            prev_title: this.props.t("common.table.pagination.prev_title"),
            next: this.props.t("common.table.pagination.next"),
            next_title: this.props.t("common.table.pagination.next_title")
          }
        }
      }
    }
  };

  componentDidMount() {
    // Call parent componentDidMount first
    super.componentDidMount();
    this._isMounted = true;
  }

  componentWillUnmount() {
    // Call parent componentWillUnmount first
    super.componentWillUnmount();
    this._isMounted = false;
  }

  /**
   * Safely execute tabulator methods with proper checks
   * @param {Function} callback - Function to execute if table is safe to use
   */
  safeExecuteOnTable = (callback) => {
    if (!this._isMounted || !this.ref?.current) {
      return;
    }
    
    try {
      // Additional check to ensure the table element is still in DOM
      const tableElement = this.ref.current.element;
      if (tableElement && tableElement.offsetParent !== null) {
        callback();
      }
    } catch (error) {
      console.warn('Error executing table operation:', error);
    }
  };

  onLanguageChanged = (newLanguage) => {
    this.setState({
      columns: this.getColumns(),
      options: {
        ...this.state.options,
        locale: getLocaleForTable()
      }
    }, () => {
      this.safeExecuteOnTable(() => {
        this.ref.current.setLocale(getLocaleForTable());
        this.ref.current.setColumns(this.getColumns());
      });
    });
  };

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.data !== this.props.data || 
        prevProps.t !== this.props.t ||
        prevState.currentLanguage !== this.state.currentLanguage) {
      
      this.setState({ 
        columns: this.getColumns(), 
        data: this.props.data, 
        t: this.props.t,
        options: {
          ...this.state.options,
          locale: getLocaleForTable()
        }
      });
    }
  }

  setRef(ref) {
    if (ref) {
      this.ref = ref;
      let _this = this;
      setTimeout(() => {
        _this.safeExecuteOnTable(() => {
          ref.current.setLocale(getLocaleForTable());
        });
      }, 100);

      // Set up row click handler safely
      setTimeout(() => {
        _this.safeExecuteOnTable(() => {
          _this.ref.current.on("rowClick", function (e, row) {
            _this.safeExecuteOnTable(() => {
              _this.ref.current.deselectRow();
              _this.ref.current.selectRow(row);
            });

            _this.props.rowClick(row.getData());
          });
        });
      }, 10);
    }
  }

  render() {
    const { columns, data, options } = this.state;
    return (
      <Fragment>
        <div className="row">
          <div className="col-xs-12">
            <div className="box">
              <div className="box-body">
                {data.length > 0 && (
                  <ReactTabulator
                    onRef={ref => this.setRef(ref)}
                    columns={columns}
                    data={data}
                    options={options}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </Fragment>
    );
  }
}

export default UserTable;
